/**
 * WebSocket Hook
 * Custom hook for managing Socket.IO messaging
 * Note: Connection is initialized by WebSocketProvider at app level
 */

import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import {
  websocketStatusAtom,
  clientIdAtom,
  socketIdAtom,
  connectionErrorAtom,
  isWaitingAtom,
  toolExecutionTextAtom,
  isToolExecutingAtom,
  lastRulesUsedAtom,
  segNodeStatesAtom,
  segNodeDetailsAtom,
  segNodeOrderAtom,
  activeSegIdAtom,
  lastSegExecutionTraceAtom,
  clearSegNodeStatesAtom,
  activeThinkingTextAtom,
  thinkingStepsAtom,
  type ThinkingStep,
  lastRunDetailsAtom,
  lastRunErrorAtom,
  lastRunApprovalAtom,
  type RunDetails,
  type RunApprovalTool,
  isConnectedAtom,
  sendMessageAtom,
  setMessageHandlerAtom,
  setErrorHandlerAtom,
  setToolExecutionHandlerAtom,
  setSourceStatusUpdateHandlerAtom,
  setSourceStatusErrorHandlerAtom,
  setWorkflowCompiledHandlerAtom,
  setWorkflowCompileFailedHandlerAtom,
  setThinkingEventHandlerAtom,
  setPersonaGeneratedHandlerAtom,
  setPersonaGenerationFailedHandlerAtom
} from '@/stores/websocket.store';
import type {
  MessagePayload,
  TestRuleResultPayload,
  ErrorPayload,
  ToolExecutionEventPayload,
  ThinkingEventPayload,
  SegExecutionTrace,
  SegNodeDetail,
  SourceStatusUpdatedPayload,
  SourceStatusUpdateErrorPayload,
  WorkflowCompiledPayload,
  WorkflowCompileFailedPayload,
  PersonaGeneratedPayload,
  PersonaGenerationFailedPayload
} from '@/types/websocket.types';

export interface UseWebSocketReturn {
  // State
  status: ReturnType<typeof useAtomValue<typeof websocketStatusAtom>>;
  clientId: string | null;
  socketId: string | null;
  connectionError: string | null;
  isConnected: boolean;
  isWaiting: boolean;
  toolExecutionText: string;
  isToolExecuting: boolean;
  lastRulesUsed: TestRuleResultPayload['rules_used'] | null;
  segNodeStates: Map<string, 'idle' | 'running' | 'success' | 'error'>;
  segNodeDetails: Map<string, SegNodeDetail>;
  segNodeOrder: string[];
  activeSegId: string | null;
  lastSegExecutionTrace: SegExecutionTrace | null;
  activeThinkingText: string | null;
  thinkingSteps: ThinkingStep[];
  lastRunDetails: RunDetails | null;
  lastRunError: string | null;
  lastRunApproval: RunApprovalTool[] | null;

  // Actions
  sendMessage: (payload: Omit<MessagePayload, 'requestId'>) => string | null;
  setMessageHandler: (handler: (data: TestRuleResultPayload) => void) => void;
  setErrorHandler: (handler: (data: ErrorPayload) => void) => void;
  setToolExecutionHandler: (handler: (data: ToolExecutionEventPayload) => void) => void;
  setSourceStatusUpdateHandler: (handler: (data: SourceStatusUpdatedPayload) => void) => void;
  setSourceStatusErrorHandler: (handler: (data: SourceStatusUpdateErrorPayload) => void) => void;
  setWorkflowCompiledHandler: (handler: (data: WorkflowCompiledPayload) => void) => void;
  setWorkflowCompileFailedHandler: (handler: (data: WorkflowCompileFailedPayload) => void) => void;
  setThinkingEventHandler: (handler: (data: ThinkingEventPayload) => void) => void;
  setPersonaGeneratedHandler: (handler: (data: PersonaGeneratedPayload) => void) => void;
  setPersonaGenerationFailedHandler: (handler: (data: PersonaGenerationFailedPayload) => void) => void;
  clearSegNodeStates: () => void;
}

export function useWebSocket(): UseWebSocketReturn {
  // State
  const status = useAtomValue(websocketStatusAtom);
  const clientId = useAtomValue(clientIdAtom);
  const socketId = useAtomValue(socketIdAtom);
  const connectionError = useAtomValue(connectionErrorAtom);
  const isConnected = useAtomValue(isConnectedAtom);
  const isWaiting = useAtomValue(isWaitingAtom);
  const toolExecutionText = useAtomValue(toolExecutionTextAtom);
  const isToolExecuting = useAtomValue(isToolExecutingAtom);
  const lastRulesUsed = useAtomValue(lastRulesUsedAtom);

  // SEG execution state
  const segNodeStates = useAtomValue(segNodeStatesAtom);
  const segNodeDetails = useAtomValue(segNodeDetailsAtom);
  const segNodeOrder = useAtomValue(segNodeOrderAtom);
  const activeSegId = useAtomValue(activeSegIdAtom);
  const lastSegExecutionTrace = useAtomValue(lastSegExecutionTraceAtom);
  const activeThinkingText = useAtomValue(activeThinkingTextAtom);
  const thinkingSteps = useAtomValue(thinkingStepsAtom);
  const lastRunDetails = useAtomValue(lastRunDetailsAtom);
  const lastRunError = useAtomValue(lastRunErrorAtom);
  const lastRunApproval = useAtomValue(lastRunApprovalAtom);

  // Actions
  const [, sendSocketMessage] = useAtom(sendMessageAtom);
  const setMessageHandlerAction = useSetAtom(setMessageHandlerAtom);
  const setErrorHandlerAction = useSetAtom(setErrorHandlerAtom);
  const setToolExecutionHandlerAction = useSetAtom(setToolExecutionHandlerAtom);
  const setSourceStatusUpdateHandlerAction = useSetAtom(setSourceStatusUpdateHandlerAtom);
  const setSourceStatusErrorHandlerAction = useSetAtom(setSourceStatusErrorHandlerAtom);
  const setWorkflowCompiledHandlerAction = useSetAtom(setWorkflowCompiledHandlerAtom);
  const setWorkflowCompileFailedHandlerAction = useSetAtom(setWorkflowCompileFailedHandlerAtom);
  const setThinkingEventHandlerAction = useSetAtom(setThinkingEventHandlerAtom);
  const setPersonaGeneratedHandlerAction = useSetAtom(setPersonaGeneratedHandlerAtom);
  const setPersonaGenerationFailedHandlerAction = useSetAtom(setPersonaGenerationFailedHandlerAtom);
  const clearSegNodeStatesAction = useSetAtom(clearSegNodeStatesAtom);

  const sendMessage = useCallback(
    (payload: Omit<MessagePayload, 'requestId'>) => {
      return sendSocketMessage(payload);
    },
    [sendSocketMessage]
  );

  const setMessageHandler = useCallback(
    (handler: (data: TestRuleResultPayload) => void) => {
      setMessageHandlerAction(handler);
    },
    [setMessageHandlerAction]
  );

  const setErrorHandler = useCallback(
    (handler: (data: ErrorPayload) => void) => {
      setErrorHandlerAction(handler);
    },
    [setErrorHandlerAction]
  );

  const setToolExecutionHandler = useCallback(
    (handler: (data: ToolExecutionEventPayload) => void) => {
      setToolExecutionHandlerAction(handler);
    },
    [setToolExecutionHandlerAction]
  );

  const setSourceStatusUpdateHandler = useCallback(
    (handler: (data: SourceStatusUpdatedPayload) => void) => {
      setSourceStatusUpdateHandlerAction(handler);
    },
    [setSourceStatusUpdateHandlerAction]
  );

  const setSourceStatusErrorHandler = useCallback(
    (handler: (data: SourceStatusUpdateErrorPayload) => void) => {
      setSourceStatusErrorHandlerAction(handler);
    },
    [setSourceStatusErrorHandlerAction]
  );

  const setWorkflowCompiledHandler = useCallback(
    (handler: (data: WorkflowCompiledPayload) => void) => {
      setWorkflowCompiledHandlerAction(handler);
    },
    [setWorkflowCompiledHandlerAction]
  );

  const setWorkflowCompileFailedHandler = useCallback(
    (handler: (data: WorkflowCompileFailedPayload) => void) => {
      setWorkflowCompileFailedHandlerAction(handler);
    },
    [setWorkflowCompileFailedHandlerAction]
  );

  const setThinkingEventHandler = useCallback(
    (handler: (data: ThinkingEventPayload) => void) => {
      setThinkingEventHandlerAction(handler);
    },
    [setThinkingEventHandlerAction]
  );

  const setPersonaGeneratedHandler = useCallback(
    (handler: (data: PersonaGeneratedPayload) => void) => {
      setPersonaGeneratedHandlerAction(handler);
    },
    [setPersonaGeneratedHandlerAction]
  );

  const setPersonaGenerationFailedHandler = useCallback(
    (handler: (data: PersonaGenerationFailedPayload) => void) => {
      setPersonaGenerationFailedHandlerAction(handler);
    },
    [setPersonaGenerationFailedHandlerAction]
  );

  const clearSegNodeStates = useCallback(() => {
    clearSegNodeStatesAction();
  }, [clearSegNodeStatesAction]);

  return {
    // State
    status,
    clientId,
    socketId,
    connectionError,
    isConnected,
    isWaiting,
    toolExecutionText,
    isToolExecuting,
    lastRulesUsed,
    segNodeStates,
    segNodeDetails,
    segNodeOrder,
    activeSegId,
    lastSegExecutionTrace,
    activeThinkingText,
    thinkingSteps,
    lastRunDetails,
    lastRunError,
    lastRunApproval,

    // Actions
    sendMessage,
    setMessageHandler,
    setErrorHandler,
    setToolExecutionHandler,
    setSourceStatusUpdateHandler,
    setSourceStatusErrorHandler,
    setWorkflowCompiledHandler,
    setWorkflowCompileFailedHandler,
    setThinkingEventHandler,
    setPersonaGeneratedHandler,
    setPersonaGenerationFailedHandler,
    clearSegNodeStates
  };
}
